import one from '@mailobj-browser/front/js/selectors/one.js'
import append from '@mailobj-browser/front/js/tree/append.js'
import prepend from '@mailobj-browser/front/js/tree/prepend.js'
import object from '@mailobj-browser/front/js/utils/object.js'
import resolvable from '@mailobj-browser/front/js/utils/resolvable.js'
import { render } from '../template/template.js'

const { DataTransfer, Intl } = globalThis
const { parseInt } = Number
const { fromCharCode } = String

const options = object(null, {
  style: 'unit',
  unit: 'byte',
  notation: 'compact',
  unitDisplay: 'narrow'
})

let counter = 0

export const list = async (
  input,
  uploads,
  styles
) => {
  const { elements } = styles
  const { files } = input
  const template = one('template', uploads)
  const list = one(`.${elements.xfiles_list}`, uploads)
  const dataTransfer = new DataTransfer()
  const { items } = dataTransfer
  
  for (const file of files) {
    items.add(file)
  }
  
  input.value = ''
  await listRecursive(list, template, input, items, styles)
  
  return list
}

const addDirectory = async (list, template, metadata, styles) => {
  const { elements } = styles
  const item = await render(template)
  
  addNode(item, metadata, styles)
  append(list, item)
  
  return one(`.${elements.xfiles_list}`, item)
}

const addFile = async (list, template, input, metadata, file, styles) => {
  const { elements } = styles
  const item = await render(template)
  const fieldset = addNode(item, metadata, styles)
  const clone = input.cloneNode(true)
  const size = one(`.${elements.xfiles_size}`, item)
  const dataTransfer = new DataTransfer()
  const { files, items } = dataTransfer
  
  items.add(file)
  clone.files = files
  clone.multiple = false
  append(size, format(metadata.size))
  append(list, item)
  prepend(fieldset, clone)
  rename(clone, metadata.fullPath ?? metadata.name)
}

const rename = (input, path) => {
  const { name } = input
  const suffix = encode(path)
  
  counter += 1
  input.name = name.replace('[]', `[${counter}][${suffix}]`)
}

const addNode = (
  item,
  metadata,
  styles
) => {
  const { elements } = styles
  const fieldset = one(`fieldset`, item)
  const path = one(`.${elements.xfiles_path}`, item)
  
  append(path, metadata.name)
  
  return fieldset
}

const encode = path =>
  encodeURIComponent(path)
    .replace(/['()*]/g, (c) => '%' + c.charCodeAt(0).toString(16))
    .replace(/%(7C|60|5E)/g, (str, hex) => fromCharCode(parseInt(hex, 16)))

const format = size =>
  new Intl.NumberFormat([], options)
    .format(size)

const getEntries = async reader => {
  const [promise, { resolve }] = resolvable()
  
  reader.readEntries(resolve)
  
  return promise
}

const getFile = async entry => {
  const [promise, { resolve }] = resolvable()
  
  entry.file(resolve)
  
  return promise
}

const listDirectory = async (list, template, input, reader, styles) => {
  while (true) {
    const entries = await getEntries(reader)
    const { length } = entries
    
    if (!length) {
      break
    }
    
    for (const entry of entries) {
      await listEntry(list, template, input, entry, styles)
    }
  }
}

const listEntry = async (list, template, input, entry, styles) => {
  const { isDirectory, isFile, fullPath, name } = entry
  
  if (isDirectory) {
    const subList = await addDirectory(list, template, { fullPath, name }, styles)
    
    await listDirectory(subList, template, input, entry.createReader(), styles)
  } else if (isFile) {
    const file = await getFile(entry)
    const { size } = file
    
    await addFile(list, template, input, { fullPath, name, size }, file, styles)
  }
}

const listRecursive = async (list, template, input, items, styles) => {
  for (const item of items) {
    const { kind } = item
    
    if (kind === 'file') {
      const entry = item.webkitGetAsEntry?.()
      
      if (entry) {
        await listEntry(list, template, input, entry, styles)
      } else {
        await addFile(list, template, input, item.getAsFile(), styles)
      }
    }
  }
}
